import { useEffect, useRef } from 'react';

// Block tab close / refresh while a game / quick quiz is mid-play. The
// in-app back button uses `confirmExit()` for an explicit prompt. Callers
// pass `active=true` only when progress would actually be lost (i.e.
// phase === 'playing').
//
// We intentionally do NOT intercept `popstate` (browser back). Earlier
// versions pushed a synthetic history entry and re-pushed it on cancel,
// which polluted the history stack and confused both browser back AND
// React Router `<Link>` clicks (the logo + side-menu nav appeared
// "stuck"). Tab close / refresh is the only path that genuinely loses
// state without a chance to confirm — that's all we guard now.
export function useExitGuard(active, message = 'Quit the game now? Your progress will be lost.') {
  const messageRef = useRef(message);
  useEffect(() => { messageRef.current = message; }, [message]);

  useEffect(() => {
    if (!active) return undefined;
    const handler = (e) => {
      e.preventDefault();
      // Modern browsers ignore the custom string but require returnValue to
      // be set on the event for the dialog to show at all.
      e.returnValue = messageRef.current;
      return messageRef.current;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [active]);
}

// Imperative helper for callers (e.g. an in-app "Back" button click).
// Returns true if the user confirmed they want to leave.
export function confirmExit(active, message = 'Quit the game now? Your progress will be lost.') {
  if (!active) return true;
  // eslint-disable-next-line no-alert
  return window.confirm(message);
}
